<template>
  <div>
    <v-card class="vms-surface" elevation="0">
      <v-toolbar flat class="px-4 pt-4 pb-2">
        <v-toolbar-title class="vms-page-title">
          <span>Branches</span>
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <div>
          <v-select 
            v-model="selectedHeaders" 
            :items="headers" 
            label="Column Visibility" 
            multiple 
            outlined 
            return-object
            dense
            hide-details
            @change="toggleColumnVisibility"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 2" class="grey--text caption">(+{{ selectedHeaders.length - 2 }} others)</span>
            </template>
         </v-select>
        </div>

        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot="{ hover }">
              <v-btn :class="{ 'info--text': hover }" icon v-bind="attrs" v-on="on" @click="createOrgModal">
                <v-icon color="info">mdi-source-branch-plus</v-icon>
              </v-btn>
            </v-hover>
          </template>
          <span class="text-caption">Add Branch</span>
        </v-tooltip>

      </v-toolbar>

      <v-divider></v-divider>

      <VmsResponsiveList :items="items" :loading="loading" loading-text="Loading branches…">
        <template #table>
          <v-data-table
            :headers="computedHeaders" 
            :items="items" 
            sort-by="name" 
            class="elevation-0"
            :loading="loading"
            loading-text="Loading... Please wait" 
            :search="filterReportBySearch" 
            show-select 
            :single-select="singleSelect"
            v-model="selected"
            :options.sync="options"
          >
            <template v-slot:[`item.index`]="{ index }">
              {{ ++index }}
            </template>

            <template v-slot:[`item.organization`]="{ item }">
              <span @click="selectOrganization(item, 4)" class="row-pointer info--text" role="button">{{ item.legal_name }}</span>
            </template>

            <template v-slot:[`item.facility`]="{ item }">
              <v-list dense>
                <v-list-item
                  v-for="(facility, i) in item.facility"
                  :key="facility.id"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ ++i + '. ' + facility.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
            
            <template v-slot:[`item.more`]="{ item }">
              <VmsIconAction icon="mdi-dots-horizontal" label="Open" @click="selectOrganization(item, 2)" />
            </template>

            <template v-slot:[`item.edit`]="{ item }">
              <VmsIconAction icon="mdi-pencil-outline" label="Edit" @click="editItem(item)" :disabled="item.attributes?.isParentOrganization == 1 ? true : false" />
            </template>

            <template v-slot:[`item.delete`]="{ item }"> 
              <VmsIconAction icon="mdi-delete-outline" label="Delete" color="error" @click="openDeleteDialog(item)" :disabled="item.attributes?.isParentOrganization == 1 ? true : false" />
            </template>

            <template v-slot:no-data>
              <a class="info--text text--darken-1">
                No Branches
              </a>
            </template>
          </v-data-table>
        </template>

        <template #card="{ item }">
          <div class="vms-list-card__title">
            <span @click="selectOrganization(item, 4)" class="row-pointer info--text" role="button">{{ item.legal_name }}</span>
          </div>
          <div class="vms-list-card__row" v-if="item.facility && item.facility.length > 0">
            <span class="vms-list-card__label">Facilities</span>
            <span>
              <span v-for="(facility, i) in item.facility" :key="facility.id">
                {{ facility.name }}<span v-if="i < item.facility.length - 1">, </span>
              </span>
            </span>
          </div>
          <div class="vms-list-card__actions">
            <VmsIconAction icon="mdi-dots-horizontal" label="Open" @click="selectOrganization(item, 2)" />
            <VmsIconAction icon="mdi-pencil-outline" label="Edit" @click="editItem(item)" :disabled="item.attributes?.isParentOrganization == 1 ? true : false" />
            <VmsIconAction icon="mdi-delete-outline" label="Delete" color="error" @click="openDeleteDialog(item)" :disabled="item.attributes?.isParentOrganization == 1 ? true : false" />
          </div>
        </template>

        <template #empty>
          <div class="vms-empty">
            <v-icon large color="grey lighten-1">mdi-source-branch</v-icon>
            <p class="mt-2 grey--text px-4">No Branches</p>
          </div>
        </template>
      </VmsResponsiveList>
    </v-card>

    <div class="text-center">
      <v-dialog v-model="showDeleteDialog" persistent width="400">
        <v-card>
          <v-card-title class="headline">
            Proceed with caution!
            <span class="float-right">
              <v-icon class="mdi mdi-alert-circle-outline"></v-icon>
            </span>
          </v-card-title>
          <v-card-text>
            You are about to delete a branch, are you sure you want to delete this item?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green darken-1" text @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" text @click="deleteOrganization(organizationToDelete)">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <v-row justify="center">
      <!-- Edit modal  -->
      <v-dialog v-model="dialog_org" max-width="600">
        <v-card>
          <v-card-title class="grey--text text-h5">
            Organization
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="mb-0 pb-0">
            <v-form ref="form" v-model="valid" lazy-validation class="pt-7 pb-0">
              <v-text-field 
                v-model="editedItem.legal_name" 
                label="Name" 
                :rules="rules.required" 
                color="info"
                class="mx-1" 
                outlined 
                dense
              ></v-text-field>

              <v-autocomplete 
                dense 
                v-model="editedItem.admin_id" 
                :items="organizationUsers" 
                item-value="id"
                item-text="attributes.name" 
                label="Location Admin" 
                :error-messages="apiErrors.admin_id" 
                outlined
              ></v-autocomplete>

              <v-radio-group v-model="editedItem.isFacilityManager" class="mt-0">
                <template v-slot:label>
                  <div>Can {{ editedItem.legal_name != null ? editedItem.legal_name : 'organization' }} manage a
                    <strong>facility?</strong></div>
                </template>
                <v-radio v-bind:value=1>
                  <template v-slot:label>
                    <div>Yes, will manage facility/ies.</div>
                  </template>
                </v-radio>
                <v-radio v-bind:value=0>
                  <template v-slot:label>
                    <div>No facility/ies to manage.</div>
                  </template>
                </v-radio>
              </v-radio-group>

              <v-autocomplete 
                v-model="editedItem.facility_id" 
                v-if="editedItem.isFacilityManager && editedItem.isFacilityManager == 1"
                :items="organizationFacilities" 
                item-value="id"
                item-text="attributes.facility" 
                label="Facility" 
                :error-messages="apiErrors.facility_id" 
                multiple
                chips
                outlined
              ></v-autocomplete>

              <v-radio-group v-model="editedItem.isParentOrganization" class="mt-0" v-if="editedItem.legal_name">
                <template v-slot:label>
                  <div>Is {{ editedItem.legal_name != null ? editedItem.legal_name : 'organization' }} a
                  <strong>parent organization?</strong></div>
                </template>
                <v-radio v-bind:value=1>
                  <template v-slot:label>
                    <div>Yes.</div>
                  </template>
                </v-radio>
                <v-radio v-bind:value=0>
                  <template v-slot:label>
                    <div>No.</div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-btn color="red darken-1" class="text-capitalize mx-1" dark text @click="dialog_org = false">
              Cancel
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn v-if="!update_org" color="green" dark elevation="0" class="text-capitalize mx-1" :loading="loading"
              @click="save">
              {{ update_org ? 'Update' : 'Save' }}
            </v-btn>

            <v-btn v-else color="green" dark elevation="0" class="text-capitalize mx-1" :loading="loading"
              @click="updateOrganization">
              Update
            </v-btn>

          </v-card-actions>

        </v-card>
      </v-dialog>
      <!-- End of modal  -->
    </v-row>
  </div>
</template>
<script>
import Visitor from '../../models/visitor';
import OrganizationService from '../../services/organization.service';
import moment from 'moment';
import FacilitiesService from '../../services/facilities.service';
import VmsResponsiveList from '../../components/VmsResponsiveList.vue';
import VmsIconAction from '../../components/VmsIconAction.vue';

export default {
  name: "Branches",
  components: { VmsResponsiveList, VmsIconAction },

  props: [
    'organization',
  ],

  data() {
    return {
      organizationToDelete: null,
      selected: [],
      singleSelect: false,
      filterReportBySearch: null,
      options: {},
      loading: false,
      selectedHeaders: [],
      editedIndex: -1,
      editedItem: {},
      defaultItem: {},
      update_org: false,
      dialog_org: false,
      submitting: false,
      apiErrors: [],
      organizationUsers: [],
      allOrganizations: [],
      rules: {
        select: [(v) => !!v || "An item should be selected"],
        select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
        required: [
          (v) => !!v || "Field is required",
          (v) => (v && v.length >= 3) || "Min 3 characters",
        ],
        file: [
          (v) => !!v || "File is required",
          (v) => (v && v.length > 0) || "File is required",
        ],
      },
      headers: [
        {
          text: "#",
          align: "start",
          filterable: true,
          value: "index",
          visible: true,
        }, 
        {
          text: "Branch Name",
          value: "organization",
          visible: true,
        },
        {
          text: "Facilities",
          value: "facility",
          visible: true,
        },
        {
          text: "Visitors",
          align: "center",
          value: "more",
          sortable: false,
          visible: true,
        },
        {
          text: "Edit",
          align: "center",
          value: "edit",
          sortable: false,
          visible: true,
        },
        {
          text: "Delete",
          align: "center",
          value: "delete",
          sortable: false,
          visible: true,
        },
      ],
      valid: false,
      items: [],
      showDeleteDialog: false,
      organizationFacilities: [],
      currentOrganization: null,
    }
  },

  computed: {
    organizationFacility() {
      return this.organization.facility[0] ?? { 'id': null, 'facility': null };
    },

    visitsWithIndex() {
      if ((this.visits).length == 0) {
        return [];
      }

      return this.visits.map(
        (visits, index) => ({
          ...visits,
          index: index + 1
        })
      );
    },

    computedHeaders() {
      let headers = this.headers.filter((item) => (item.visible == true));
      return headers;
    },
  },

  created() {
    this.$nextTick(() => {
      this.selectedHeaders = this.computedHeaders;
      this.getData();
      this.getAllOrganizations();
      this.getUsers();
      this.getAllFacilities();
    });
  },

  methods: {
    openDeleteDialog(item) {
      this.organizationToDelete = item;
      this.showDeleteDialog = true;
    },

    toggleColumnVisibility() {
      if (this.selectedHeaders && this.selectedHeaders.length > 0) {
        this.headers.forEach((visitHeader) => {
          let index = -1;
          index = this.headers.findIndex((header) => header.value == visitHeader.value);
          if (index > -1) {
            this.headers[index].visible = false;
          }
        });

        this.selectedHeaders.forEach((selectedHeader) => {
          let index = -1;
          index = this.headers.findIndex((header) => header.value == selectedHeader.value);
          if (index > -1) {
            this.headers[index].visible = true;
          }
        });
      }
    },

    getAllFacilities() {
      let organizationId = typeof this.currentOrganization?.id != 'undefined' ? this.currentOrganization?.id : this.organization?.id;
      FacilitiesService.allFacilities(organizationId).then(
        (response) => {
          if (response.status == 200) {
            this.organizationFacilities = response.data.data;
            this.loading = false;
          }
        },
        (error) => {
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    editItem(item) {
      this.formTitle = 'Update Organization';
      this.editedIndex = this.items.indexOf(item);
      this.editedItem.id = item.id;
      this.editedItem.legal_name = item.attributes.legal_name // Object.assign({}, item);
      this.editedItem.isFacilityManager = item.attributes.isFacilityManager;
      this.editedItem.isParentOrganization = item.attributes.isParentOrganization;
      this.editedItem.isThirdParty = item.attributes.isThirdParty;
      this.editedItem.id_parent = item.attributes.id_parent?.toString();
      this.editedItem.facility_id = item.facility?.map(facility => facility.id);

      if (item.relationships.admin != null) {
        this.editedItem.admin_id = item.relationships.admin.id;
      }

      this.update_org = true;
      this.dialog_org = true;
    },

    // deleting the data from the database
    deleteOrganization(item) {
      OrganizationService.delete(item).then(
        (response) => {
          if (response.status === 200) {
            let index = this.items.findIndex(org => org.id === item.id);
            if (index !== -1) {
              this.items.splice(index, 1);
            }

            this.$store.dispatch("alert/success", response?.data?.message ?? "Branch Deleted successfully");
            this.getData();
            this.close();
          } else {
            this.$store.dispatch("alert/error", response?.data?.message ?? "Branch not deleted");
          }
        },
        (error) => {
          if (error.response.status === 422) {
            this.$store.dispatch("alert/error", error.response.data.message);
          }

          if (error.response.status === 401) {
            this.handleLogout();
          }

          this.$store.dispatch("alert/error", error.response.data.message);
        }
      );
    },

    close() {
      this.update_org = false;
      this.dialog_org = false;
      this.showDeleteDialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    save() {
      this.loading = true;
      this.$refs.form.validate();
      if (this.valid) {
        // create
        this.editedItem.isFacilityManager = this.editedItem.isFacilityManager != null ? 1 : 0

        OrganizationService.store(this.editedItem).then(
          (response) => {

            if (response.status == 200) {
              this.items.push(this.editedItem);
              this.$store.dispatch("alert/success", this.editedItem.legal_name + " created");
              this.loading = false;
              this.getData();
              this.close();
            } else {
              this.$store.dispatch("alert/error", this.editedItem.legal_name + " not created");
              this.getData();
            }
          },
          (error) => {
            if (error.response.status == 422) {

              this.apiErrors = error.response.data.errors
              this.$store.dispatch(
                "alert/error",
                error.response.data.message
              );
              this.loading = false;
              this.getData();
            }
            if (error.response.status == 401) {
              this.handleLogout();
              this.loading = false;
              this.getData();
            }
            console.log(error);
            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );

      } else {
        this.loading = false;
        this.getData();
      }
    },

    updateOrganization() {
      this.loading = true;
      this.$refs.form.validate();
      if (this.valid) {
        // edit
        this.editedItem.isFacilityManager = this.editedItem.isFacilityManager != null ? 1 : 0
        OrganizationService.update(this.editedItem).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch(
                "alert/success",
                "Item edited successfully",
                response.data.message
              );
              this.loading = false;
              this.getData();
              this.close();
            } else {
              this.$store.dispatch("alert/error", response.data.message);
              this.getData();
              this.loading = false;
            }
          },
          (error) => {
            if (error.response.status == 422) {
              this.loading = false;
              this.$store.dispatch(
                "alert/error",
                error.response.data.message
              );
              this.getData();
            }
            if (error.response.status == 401) {
              this.handleLogout();
              this.loading = false;
              this.getData();
            }
            console.log(error);
            this.loading = false;
            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      } else {
        this.loading = false;
        this.getData();
      }
    },

    createOrgModal() {
      this.dialog_org = true;
      this.formTitle = "New branch";
      this.editedItem.id_parent = this.organization.id;
    },

    formatDate(date, format = 'DD-MMM-YYYY') {
      return moment(date).format(format);
    },

    // Fetch organization users
    getUsers() {
      OrganizationService.all_organization_users().then(
        success => {
          this.organizationUsers = success.data ? success.data.data : this.organizationUsers;
        },
        error => {
          console.log(error)
          this.organizationUsers = [];
        }
      )
    },

    // fetch data from the database
    getData() {
      this.loading = true;
      let organizationId = typeof this.currentOrganization?.id != 'undefined' ? this.currentOrganization?.id : this.organization?.id;
      OrganizationService.personBranches(organizationId).then(
        (response) => {
          if (response.status == 200) {
            this.items = response.data.data;
            this.loading = false;
          } else {
            console.log(response.data.error);
            this.loading = false;
          }
        },
        (error) => {
          this.items = [];
          this.loading = false;

          console.log(error);
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    getAllOrganizations() {
      OrganizationService.parentIndex().then(
        (response) => {
          if (response.status == 200) {
            this.allOrganizations = response.data.data;
            this.loading = false;
          } else {
            console.log(response.data.error);
          }
        },
        (error) => {
          this.allOrganizations = [];
          console.log(error);
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    closeModal() {
      this.phoneNumber = null;
      this.visitor = new Visitor();
      this.showScheduleModal = false;
    },

    selectOrganization(organization, tabId = 1) {
      this.editedItem = Object.assign({}, organization);
      this.$emit('select_organization', { selectedTabId: tabId, selectedOrganization: organization });
    },
  },
  watch: {
    options: {
      handler () {
        this.getData();
      },
      deep: true,
    },
    organization: {
      handler () {
        this.getData();
      },
      deep: true,
    },
    currentOrganization: {
      handler () {
        this.getData();
      },
      deep: true,
    },
  },
}
</script>